/**
 * AdminOverview — KPI dashboard at /admin/overview. Everything is one
 * GET /admin/overview call; no client-side aggregation.
 *
 * Layout:
 *   - 4 KPI cards (MTD spend, Finalized 7d, Avg cost/meeting, Roster)
 *     with week-over-week deltas on the first three, click-through
 *     to the matching detail tab. Auto-refreshes every 60s; each
 *     card gets a brief pine-ring pulse when its value changes.
 *   - Daily activity section: two stacked sparklines (pine $ bars on
 *     top, terracotta meeting bars on bottom).
 *   - Roster status: Graph + manual sources, sync timestamps,
 *     headshot + title coverage.
 *   - Recent errors: last 5 rows from error_log.
 *   - Rate-limit pressure: users at ≥80% of their daily limit today.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/apiClient.ts';

interface OverviewData {
  mtdSpendUsd: number;
  mtdSpendPrevUsd: number;
  finalizedThisWeek: number;
  finalizedThisMonth: number;
  finalizedPrev7d: number;
  avgCostPerMeetingUsd: number | null;
  avgCostPerMeetingPrevUsd: number | null;
  /** Lifetime totals — every usage_log row + every finalized meeting,
   *  with no time bound. Powers the "Lifetime cost / meeting" KPI
   *  card. Lets admins see a stable cost-per-meeting figure that
   *  doesn't bounce around on a slow / busy month the way the
   *  MTD-only `avgCostPerMeetingUsd` does. */
  lifetimeSpendUsd: number;
  lifetimeFinalizedCount: number;
  avgCostPerMeetingLifetimeUsd: number | null;
  dailySpend: Array<{ date: string; costUsd: number }>;
  dailyMeetings: Array<{ date: string; count: number }>;
  recentErrors: Array<{
    id: number;
    level: 'warn' | 'error' | 'fatal';
    message: string;
    createdAt: string;
  }>;
  rateLimitPressure: Array<{
    userId: string;
    displayName: string;
    email: string;
    todaySpend: number;
    limit: number;
    pctUsed: number;
  }>;
  errorCountLast7d: number;
  roster: {
    lastScrapeAt: string | null;
    lastGraphSyncAt: string | null;
    graphRows: number;
    scraperRows: number;
    manualRows: number;
    rowsWithHeadshot: number;
    rowsWithTitle: number;
  };
}

export function AdminOverview() {
  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api.get<OverviewData>('/admin/overview'),
    // Auto-refresh every 60s so the dashboard feels live without
    // hammering the API. Pauses when the tab is in the background
    // (React Query default) to avoid wasted cycles.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  // ── Pulse detection — per-card flash when a numeric value changes
  // between refetches. Tracks previous data in a ref so we can diff
  // on each successful fetch. ─────────────────────────────────────
  const prevRef = useRef<OverviewData | null>(null);
  const [pulseKeys, setPulseKeys] = useState<Set<string>>(new Set());
  useEffect(() => {
    const next = overview.data;
    if (!next) return;
    const prev = prevRef.current;
    prevRef.current = next;
    if (!prev) return;
    const changed = new Set<string>();
    if (prev.mtdSpendUsd !== next.mtdSpendUsd) changed.add('mtdSpend');
    if (prev.finalizedThisWeek !== next.finalizedThisWeek) {
      changed.add('finalized');
    }
    if (prev.avgCostPerMeetingUsd !== next.avgCostPerMeetingUsd) {
      changed.add('avgCost');
    }
    if (prev.avgCostPerMeetingLifetimeUsd !== next.avgCostPerMeetingLifetimeUsd) {
      changed.add('avgCostLifetime');
    }
    const prevRoster =
      prev.roster.graphRows + prev.roster.scraperRows + prev.roster.manualRows;
    const nextRoster =
      next.roster.graphRows + next.roster.scraperRows + next.roster.manualRows;
    if (prevRoster !== nextRoster) changed.add('roster');
    if (changed.size > 0) {
      setPulseKeys(changed);
      const t = setTimeout(() => setPulseKeys(new Set()), 1400);
      return () => clearTimeout(t);
    }
    // No-op return when nothing changed — keeps every code path in
    // this effect returning the same shape. Silences TS7030.
    return undefined;
  }, [overview.data]);

  if (overview.isLoading) {
    return (
      <div className="py-10 text-center text-sm italic text-charcoal/50">
        Loading overview…
      </div>
    );
  }
  if (overview.error) {
    return (
      <div className="rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 p-4 text-[13px] text-terra-cotta">
        {String((overview.error as Error).message)}
      </div>
    );
  }
  const d = overview.data!;
  const rosterTotal =
    d.roster.graphRows + d.roster.scraperRows + d.roster.manualRows;

  return (
    // Mockup .admin-section uses mb-4 (16px) between sections.
    // space-y-6 (24px) was too generous; tightened to match.
    <div className="space-y-4">
      {/* Headline KPIs — mockup .kpi-grid uses gap-3.5 (14px).
          Five cards: stack 1-up on mobile, 3-up on tablet (3 then 2
          on the second row), 5-up on lg+ so a wide monitor still
          gets the full row at a glance. */}
      <div className="grid grid-cols-1 gap-3.5 md:grid-cols-3 lg:grid-cols-5">
        <KpiCard
          label="MTD AI spend"
          value={`$${d.mtdSpendUsd.toFixed(2)}`}
          hint="Claude API cost this month"
          delta={deltaPct(d.mtdSpendUsd, d.mtdSpendPrevUsd)}
          deltaLabel="vs. same window last month"
          deltaDir="lower-is-better"
          pulse={pulseKeys.has('mtdSpend')}
          to="/admin/users"
        />
        <KpiCard
          label="Finalized this week"
          value={d.finalizedThisWeek}
          hint={`${d.finalizedThisMonth} this month`}
          delta={deltaPct(d.finalizedThisWeek, d.finalizedPrev7d)}
          deltaLabel="vs. previous 7 days"
          deltaDir="higher-is-better"
          pulse={pulseKeys.has('finalized')}
          to="/admin/meetings"
        />
        <KpiCard
          label="Avg cost / meeting"
          value={
            d.avgCostPerMeetingUsd == null
              ? '-'
              : `$${d.avgCostPerMeetingUsd.toFixed(2)}`
          }
          hint="MTD spend ÷ MTD finalized"
          delta={
            d.avgCostPerMeetingUsd != null &&
            d.avgCostPerMeetingPrevUsd != null
              ? deltaPct(
                  d.avgCostPerMeetingUsd,
                  d.avgCostPerMeetingPrevUsd,
                )
              : null
          }
          deltaLabel="vs. last full month"
          deltaDir="lower-is-better"
          pulse={pulseKeys.has('avgCost')}
        />
        {/* Lifetime cost / meeting — all-time spend ÷ all-time
            finalized count. No delta because there is no
            previous-lifetime baseline. Sub-line shows the raw
            inputs so an admin can sanity-check the math. */}
        <KpiCard
          label="Lifetime cost / meeting"
          value={
            d.avgCostPerMeetingLifetimeUsd == null
              ? '-'
              : `$${d.avgCostPerMeetingLifetimeUsd.toFixed(2)}`
          }
          hint={
            d.lifetimeFinalizedCount > 0
              ? `$${d.lifetimeSpendUsd.toFixed(2)} ÷ ${d.lifetimeFinalizedCount} meeting${d.lifetimeFinalizedCount === 1 ? '' : 's'}`
              : 'No finalized meetings yet'
          }
          pulse={pulseKeys.has('avgCostLifetime')}
        />
        <KpiCard
          label="Roster"
          value={rosterTotal}
          hint={
            d.roster.graphRows > 0
              ? `${d.roster.graphRows} graph · ${d.roster.manualRows} manual${d.roster.scraperRows > 0 ? ` · ${d.roster.scraperRows} scraped` : ''}`
              : `${d.roster.scraperRows} scraped · ${d.roster.manualRows} manual`
          }
          pulse={pulseKeys.has('roster')}
          to="/admin/team"
        />
      </div>

      {/* Daily activity (stacked sparklines) */}
      <Section
        title="Daily activity"
        subtitle="Last 30 days, America/New_York. Hover any bar for exact values."
      >
        <div className="space-y-4">
          <DailyBars
            label="Spend"
            unit="$"
            accent="pine"
            data={padTo30Days(
              d.dailySpend.map((x) => ({ date: x.date, value: x.costUsd })),
            )}
            formatValue={(n) => `$${n.toFixed(2)}`}
          />
          <DailyBars
            label="Meetings finalized"
            unit=""
            accent="terracotta"
            data={padTo30Days(
              d.dailyMeetings.map((x) => ({ date: x.date, value: x.count })),
            )}
            formatValue={(n) => `${n}`}
          />
        </div>
      </Section>

      {/* Roster status */}
      <Section
        title="Roster status"
        subtitle="Who's in ojb_team. Graph sync is primary; ojb.com scrape is a fallback. Both are admin-triggered from the OJB Team tab."
      >
        {/* Mockup .dl-grid puts each term-value pair on its own row
            (display:contents on .dl-row so the grid sees dt+dd as two
            cells in a 1fr_1fr grid). Switched from md:grid-cols-2 (2
            stat-pairs side-by-side) to a single-column flex stack so
            5 stats render as 5 vertical rows, matching the mockup. */}
        <dl className="flex flex-col">
          <Row
            term="Last Graph sync"
            value={
              d.roster.lastGraphSyncAt
                ? new Date(d.roster.lastGraphSyncAt).toLocaleString()
                : '- never -'
            }
          />
          <Row
            term="Graph rows"
            value={d.roster.graphRows.toString()}
            accent
          />
          <Row term="Manual rows" value={d.roster.manualRows.toString()} />
          <Row
            term="With headshot"
            value={formatCoverage(d.roster.rowsWithHeadshot, rosterTotal)}
            warn={
              rosterTotal > 0 &&
              d.roster.rowsWithHeadshot / rosterTotal < 0.7
            }
          />
          <Row
            term="With job title"
            value={formatCoverage(d.roster.rowsWithTitle, rosterTotal)}
            warn={
              rosterTotal > 0 && d.roster.rowsWithTitle / rosterTotal < 0.5
            }
          />
          {d.roster.scraperRows > 0 && (
            <Row
              term="Legacy scraper rows"
              value={`${d.roster.scraperRows} (consider merging)`}
              warn
            />
          )}
        </dl>
      </Section>

      {/* Recent errors */}
      <Section
        title="Recent errors"
        subtitle={`${d.errorCountLast7d} total in the last 7 days. Full context in API server logs.`}
      >
        {d.recentErrors.length === 0 ? (
          <div className="py-3 text-center text-[12px] italic text-charcoal/55">
            No errors logged. ✓
          </div>
        ) : (
          // Mockup .error-list / .error-row / .error-pill — pill +
          // message + timestamp. Pill colors: fatal red-tinted, error
          // amber, warn neutral charcoal. (Switched fatal from solid
          // red to terra-cotta-tinted so it stays on-brand.)
          <ul className="flex flex-col gap-2">
            {d.recentErrors.map((err) => (
              <li key={err.id} className="flex items-start gap-2.5 py-1.5">
                <span
                  className={
                    'mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] ' +
                    (err.level === 'fatal'
                      ? 'bg-terra-cotta/15 text-terra-cotta'
                      : err.level === 'error'
                        ? 'bg-amber-300/30 text-amber-900'
                        : 'bg-charcoal/10 text-charcoal/70')
                  }
                >
                  {err.level}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12px] text-charcoal">
                    {err.message}
                  </div>
                  <div className="mt-0.5 text-[11px] text-charcoal/50">
                    {new Date(err.createdAt).toLocaleString()}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Rate limit pressure */}
      <Section
        title="Rate limit pressure"
        subtitle="Users at ≥80% of their daily AI spend limit (resets 00:00 America/New_York). Raise a limit from the Users tab."
      >
        {d.rateLimitPressure.length === 0 ? (
          <div className="py-3 text-center text-[12px] italic text-charcoal/55">
            Nobody's approaching their limit right now.
          </div>
        ) : (
          // Mockup .rlp-row family. Bar fill: pine when under 95%,
          // amber #E5A62E in the 95-99% range, terra-cotta when over
          // (was red-500). Same color for the over-cap dollar number.
          <ul className="flex flex-col">
            {d.rateLimitPressure.map((p) => {
              const pct = Math.min(1, p.pctUsed);
              const barColor =
                pct >= 1
                  ? 'bg-terra-cotta'
                  : pct >= 0.95
                    ? 'bg-amber-500'
                    : 'bg-pine';
              return (
                <li key={p.userId} className="py-2">
                  <div className="mb-1 flex items-baseline justify-between gap-3 text-[12px]">
                    <Link
                      to="/admin/users"
                      className="truncate font-medium text-charcoal no-underline transition-colors hover:text-pine hover:underline"
                    >
                      {p.displayName || p.email}
                    </Link>
                    <span className="shrink-0 tabular-nums">
                      <b
                        className={
                          'font-bold ' +
                          (pct >= 1 ? 'text-terra-cotta' : 'text-charcoal')
                        }
                      >
                        ${p.todaySpend.toFixed(2)}
                      </b>
                      <span className="text-charcoal/45">
                        {' '}
                        / ${p.limit.toFixed(2)}
                      </span>
                      <span className="ml-1.5 text-charcoal/50">
                        ({Math.round(pct * 100)}%)
                      </span>
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-charcoal/10">
                    <div
                      className={`h-full rounded-full ${barColor} transition-all`}
                      style={{ width: `${pct * 100}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </div>
  );
}

function formatCoverage(n: number, total: number): string {
  if (total === 0) return `${n} / 0`;
  const pct = Math.round((n / total) * 100);
  return `${n} / ${total} (${pct}%)`;
}

/** Returns a signed percentage delta (+12, -30, 0) or null when the
 *  baseline is zero and the new value is zero (nothing to compare). */
function deltaPct(current: number, previous: number): number | null {
  if (previous === 0) {
    if (current === 0) return 0;
    // Growing from zero - surface as a "new activity" badge rather
    // than an infinite delta.
    return null;
  }
  return ((current - previous) / previous) * 100;
}

/* ─── Building blocks ──────────────────────────────────────────────── */

function KpiCard({
  label,
  value,
  hint,
  tone = 'default',
  delta,
  deltaLabel,
  deltaDir,
  pulse = false,
  to,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'default' | 'warn';
  delta?: number | null;
  deltaLabel?: string;
  /** When "lower-is-better" a negative delta renders pine/green; when
   *  "higher-is-better" a positive delta is green. */
  deltaDir?: 'higher-is-better' | 'lower-is-better';
  pulse?: boolean;
  /** If provided, the whole card becomes a Link with hover cue. */
  to?: string;
}) {
  // Mockup .kpi-card: cloud/60 bg + charcoal/10 border + rounded-2xl
  // + 18/20 padding + 28px Aeonik 600 pine value. Warn tone (amber)
  // for surfaces that need attention (e.g. budget breach approaching).
  const toneStyles =
    tone === 'warn'
      ? 'border-amber-400/40 bg-amber-100/40 hover:border-amber-500/60 hover:bg-amber-100/60'
      : 'border-charcoal/10 bg-cloud/60 hover:border-pine/30 hover:bg-cloud/85';
  const numeralColor =
    tone === 'warn' ? 'text-amber-800' : 'text-pine';

  const inner = (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-charcoal/60">
          {label}
        </div>
        {to && (
          <span
            aria-hidden
            className="text-[14px] leading-none text-charcoal/30 transition-colors group-hover:text-pine"
          >
            →
          </span>
        )}
      </div>
      {/* Value - 28px Aeonik 600 pine (mockup .kpi-card__value).
          Tabular nums so the column reads as a stat sheet, not text. */}
      <div
        className={`mt-2 text-[28px] font-semibold leading-none tabular-nums ${numeralColor}`}
      >
        {value}
      </div>
      {delta != null && (
        <div className="mt-2 flex items-baseline gap-1.5 text-[11px]">
          <DeltaBadge delta={delta} direction={deltaDir ?? 'higher-is-better'} />
          {deltaLabel && (
            <span className="text-charcoal/45">{deltaLabel}</span>
          )}
        </div>
      )}
      {hint && (
        <div className="mt-1.5 text-[12px] text-charcoal/55">{hint}</div>
      )}
    </>
  );

  // Pulse ring on value-change - pine/40 ring stays for ~1.4s in the
  // parent's pulseKeys map. Tightened to ring-1 inset so the card
  // doesn't visually shift on pulse.
  const baseClasses =
    'group block rounded-2xl border px-5 py-[18px] shadow-[0_1px_2px_rgba(43,42,47,0.04)] transition-all duration-700 ' +
    toneStyles +
    ' ' +
    (pulse ? 'ring-2 ring-pine/40 ' : 'ring-0 ring-transparent ');

  if (to) {
    return (
      <Link to={to} className={baseClasses + ' no-underline'}>
        {inner}
      </Link>
    );
  }
  return <div className={baseClasses}>{inner}</div>;
}

function DeltaBadge({
  delta,
  direction,
}: {
  delta: number;
  direction: 'higher-is-better' | 'lower-is-better';
}) {
  const rounded = Math.round(delta);
  const isUp = rounded > 0;
  const isDown = rounded < 0;
  const isFlat = rounded === 0;
  const good =
    (direction === 'higher-is-better' && isUp) ||
    (direction === 'lower-is-better' && isDown);
  const bad =
    (direction === 'higher-is-better' && isDown) ||
    (direction === 'lower-is-better' && isUp);
  // Mockup .delta-badge: good → pine, bad → terra-cotta (was red),
  // flat → muted charcoal. Arrow + abs rounded percent. Bold tabular
  // so the deltas line up cleanly across cards.
  const color = isFlat
    ? 'text-charcoal/40'
    : good
      ? 'text-pine'
      : bad
        ? 'text-terra-cotta'
        : 'text-charcoal/60';
  const arrow = isFlat ? '→' : isUp ? '↑' : '↓';
  return (
    <span className={`font-semibold tabular-nums ${color}`}>
      {arrow} {Math.abs(rounded)}%
    </span>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  // Mockup .admin-section: cloud bg + charcoal/10 + rounded-2xl(16px)
  // + 20/22 padding. Title 13px pine bold tracking-[0.10em] + 12px
  // muted subtitle.
  return (
    <section className="rounded-2xl border border-charcoal/10 bg-cloud px-[22px] py-5 shadow-[0_1px_2px_rgba(43,42,47,0.04)]">
      <h2 className="text-[13px] font-semibold uppercase tracking-[0.10em] text-pine">
        {title}
      </h2>
      <p className="mb-3.5 mt-1 text-[12px] text-charcoal/55">{subtitle}</p>
      {children}
    </section>
  );
}

function Row({
  term,
  value,
  accent = false,
  warn = false,
}: {
  term: string;
  value: string;
  accent?: boolean;
  warn?: boolean;
}) {
  // Mockup .dl-row: bottom-bordered charcoal/8 hairline. Warn → terra
  // (was amber-700, switched for brand consistency); accent → pine.
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-charcoal/8 py-2">
      <dt className="text-[12px] text-charcoal/60">{term}</dt>
      <dd
        className={
          'text-[12px] font-medium tabular-nums ' +
          (warn
            ? 'text-terra-cotta'
            : accent
              ? 'text-pine'
              : 'text-charcoal')
        }
      >
        {value}
      </dd>
    </div>
  );
}

/** Generic daily-bar chart used for both spend + meetings sparklines. */
function DailyBars({
  label,
  accent,
  data,
  formatValue,
}: {
  label: string;
  unit: string;
  accent: 'pine' | 'terracotta';
  data: Array<{ date: string; value: number }>;
  formatValue: (n: number) => string;
}) {
  if (data.length === 0) {
    return (
      <div>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-charcoal/55">
          {label}
        </div>
        <div className="py-4 text-center text-xs italic text-charcoal/45">
          No activity in the last 30 days.
        </div>
      </div>
    );
  }
  const max = Math.max(...data.map((d) => d.value), 0.01);
  const peak = data.reduce(
    (acc, d) => (d.value > acc.value ? d : acc),
    data[0],
  );
  const today = data[data.length - 1];
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const barActive =
    accent === 'pine' ? 'bg-pine' : 'bg-terra-cotta';
  const barDim =
    accent === 'pine' ? 'bg-pine/60' : 'bg-terra-cotta/60';
  return (
    <div>
      {/* Mockup .daily-bars__head: 11px label uppercase tracking-[0.12em]
          + readout right with bold accent values. Switched bold accents
          to charcoal (was valueColor) per mockup .daily-bars__readout b
          spec — readout numbers are charcoal, not the chart accent
          color, so they don't compete with the bars' accent. */}
      <div className="mb-2 flex items-baseline justify-between gap-2 text-[11px]">
        <div className="font-semibold uppercase tracking-[0.12em] text-charcoal/55">
          {label}
        </div>
        <div className="text-charcoal/70">
          Peak <b className="font-bold text-charcoal">{formatValue(peak.value)}</b>{' '}
          <span className="text-charcoal/40">on {shortDate(peak.date)}</span>
          {' · '}
          Today <b className="font-bold text-charcoal">{formatValue(today.value)}</b>
          <span className="text-charcoal/40"> (so far)</span>
          {total > 0 && (
            <>
              {' · '}
              30-day sum{' '}
              <b className="font-bold text-charcoal">{formatValue(total)}</b>
            </>
          )}
        </div>
      </div>
      {/* Bars — outer flex stretches children to full track height
          (items-stretch = default); each wrapper is a column-flex
          container that pushes its bar to the bottom via justify-end.
          Without this the percentage-height on the bar resolves
          against a 0-height parent and the bars collapse. */}
      <div className="flex gap-1" style={{ height: '64px' }}>
        {data.map((d) => {
          const pct = d.value === 0 ? 2 : Math.max(4, (d.value / max) * 100);
          const isPeak = d.date === peak.date && d.value > 0;
          return (
            <div
              key={d.date}
              className="group relative flex flex-1 flex-col justify-end"
              title={`${d.date} - ${formatValue(d.value)}`}
            >
              <div
                className={
                  'w-full rounded-t transition-colors ' +
                  (d.value === 0
                    ? 'bg-charcoal/10'
                    : isPeak
                      ? barActive
                      : barDim)
                }
                style={{ height: `${pct}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-charcoal/45">
        <span>{shortDate(data[0].date)}</span>
        <span>{shortDate(data[data.length - 1].date)}</span>
      </div>
    </div>
  );
}

/** "2026-04-24" → "Apr 24" */
function shortDate(iso: string): string {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Pad a sparse daily-series to 30 days ending today (America/New_York).
 * Backend only returns days that had activity; without padding a single-
 * meeting day renders as one full-width bar with no scale context.
 *
 * Anchored to today-in-EST (matches the server's GROUP BY timezone)
 * so the series aligns with whatever `lastScrapeAt`-style timestamps
 * the rest of the dashboard displays.
 */
function padTo30Days<T extends { date: string; value: number }>(
  data: T[],
): Array<{ date: string; value: number }> {
  const map = new Map(data.map((d) => [d.date, d.value]));
  // Intl 'en-CA' locale produces ISO-style YYYY-MM-DD, which matches
  // the format the backend's to_char(... 'YYYY-MM-DD') emits.
  const estFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const now = new Date();
  const out: Array<{ date: string; value: number }> = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
    const iso = estFmt.format(d);
    out.push({ date: iso, value: map.get(iso) ?? 0 });
  }
  return out;
}
